import React, { useEffect, useState } from 'react';

import { Modal, Form, Table, Tabs, Tab } from 'react-bootstrap';
import Button from 'components/base/Button';

import { useAppDispatch, useAppSelector } from 'redux/store';
import { createZone, updateZone, getZoneAuditTrail, type Zone, type RetentionPolicyRule } from '../../../../redux/slices/lists/zonesSlice';
import RetentionPoliciesEditor from '../customer_node_definitions/node_definition_editor/components/ui/RetentionPolicy';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

export interface EditZoneModalProps {
  show: boolean;
  handleClose: () => void;
  mode: 'create' | 'edit';
  data: Zone | null;
}

interface ZoneFormState {
  name: string;
  new_name: string;
  description: string;
  retention_policies: RetentionPolicyRule[];
  zone_id: number | null;
}

const emptyForm: ZoneFormState = { name: '', new_name: '', description: '', retention_policies: [], zone_id: null };

/**
 * Create / edit a zone. Self-contained: holds its own form state, fetches the
 * audit trail on demand, and persists via the zone thunks — so it can be opened
 * from anywhere, not just the list page. In edit mode the name field edits
 * `new_name` (a rename); `name` stays the original identity the PUT addresses.
 */
const EditZoneModal: React.FC<EditZoneModalProps> = ({ show, handleClose, mode, data }) => {
  const dispatch = useAppDispatch();
  const auditTrail = useAppSelector((state) => state.listOfZones.auditTrail);

  const [form, setForm] = useState<ZoneFormState>(emptyForm);
  const [key, setKey] = useState<'zone' | 'audit'>('zone');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDirty = useUnsavedChanges(show, form);

  // Initialise the form whenever the dialog opens.
  useEffect(() => {
    if (!show) return;
    setKey('zone');
    if (mode === 'edit' && data) {
      setForm({
        name: data.name,
        new_name: '',
        description: data.description || '',
        retention_policies: data.retention_policies || [],
        zone_id: data.zone_id,
      });
    } else {
      setForm(emptyForm);
    }
  }, [show, mode, data]);

  // Fetch the audit trail when switching to the Audit tab.
  useEffect(() => {
    if (key === 'audit' && data?.name) {
      dispatch(getZoneAuditTrail(data.name) as any);
    }
  }, [key, data, dispatch]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetAndClose = () => {
    setForm(emptyForm);
    handleClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      description: form.description,
      retention_policies: form.retention_policies,
    };
    setIsSubmitting(true);
    try {
      if (form.zone_id) {
        const result = await dispatch(updateZone({ ...payload, zone_id: form.zone_id, new_name: form.new_name }));
        if (updateZone.rejected.match(result)) return;
      } else {
        const result = await dispatch(createZone(payload));
        if (createZone.rejected.match(result)) return;
      }
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const nameField =
    mode === 'edit' ? (
      <Form.Control type="text" placeholder="Enter zone name" name="new_name" value={form.new_name ? form.new_name : form.name} onChange={handleChange} />
    ) : (
      <Form.Control type="text" placeholder="Enter zone name" name="name" value={form.name} onChange={handleChange} />
    );

  const zoneForm = (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>Zone Name</Form.Label>
        {nameField}
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control as="textarea" placeholder="Enter zone description" name="description" value={form.description} onChange={handleChange} />
      </Form.Group>
      <RetentionPoliciesEditor state={form} onChange={handleChange} />
    </Form>
  );

  return (
    <>
      <Modal show={show} onHide={handleRequestClose} className={mode === 'edit' ? 'modal-lg' : undefined}>
        <Modal.Header closeButton>
          <Modal.Title>{mode === 'edit' ? 'Edit Zone' : 'Create New Zone'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mode === 'edit' ? (
            <Tabs id="zone-audit-tabs" activeKey={key} onSelect={(k) => setKey(k as 'zone' | 'audit')} className="mb-3">
              <Tab eventKey="zone" title="Zone">
                {zoneForm}
              </Tab>
              <Tab eventKey="audit" title="Audit Trail">
                {auditTrail && auditTrail?.audit_trail?.length > 0 ? (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Type</th>
                        <th>User</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditTrail?.audit_trail?.map((audit: any, index: number) => (
                        <tr key={index}>
                          <td>{audit.created_at}</td>
                          <td>{audit.audit_details.action}</td>
                          <td>{audit.user_name}</td>
                          <td>
                            <b>Previous state:</b> <br />
                            Name: {audit.audit_details.previous_state?.name} <br />
                            Description: {audit.audit_details.previous_state?.description} <br />
                            <b>New state:</b> <br />
                            Name: {audit.audit_details.new_state?.name} <br />
                            Description: {audit.audit_details.new_state?.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p>No audit trail available</p>
                )}
              </Tab>
            </Tabs>
          ) : (
            zoneForm
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
            Save and Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          resetAndClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default EditZoneModal;
